// ─────────────────────────────────────────────────────────────────────────────
// Short-lived memory of approval requests that were already resolved.
//
// The backend's `?status=pending` list is eventually-consistent, so a refetch
// right after an approve / decline / operator-cancel can still return the
// finished request. Nothing else in the client remembers that it was handled, so
// the list would resurrect it: the record comes back, awaitingCount goes 0 → 1,
// and the approval modal pops again for something already done.
//
// Entries are keyed by approval_request id and forgotten once the request's own
// window has passed — after that the fetch's `expires_at` guard already skips it,
// so the tombstone is no longer needed and the map stays bounded.
// ─────────────────────────────────────────────────────────────────────────────

const resolved = new Map() // String(id) -> forgetAt (epoch ms, server-aligned)

// Requests carry a ~2 min window; keep a little grace past it for clock skew.
const EXPIRY_GRACE_MS = 60 * 1000
// Fallback when the resolved payload carried no expires_at.
const DEFAULT_TTL_MS = 5 * 60 * 1000

export function markApprovalResolved (id, expiresAt, now) {
  if (id == null) return
  const ref = now != null ? now : Date.now()
  const forgetAt = expiresAt != null ? expiresAt + EXPIRY_GRACE_MS : ref + DEFAULT_TTL_MS
  resolved.set(String(id), forgetAt)
}

// True while we should keep ignoring this request in list responses.
// Self-prunes as it goes, so no separate sweep is needed.
export function isApprovalResolved (id, now) {
  if (id == null) return false
  const key = String(id)
  const forgetAt = resolved.get(key)
  if (forgetAt == null) return false
  const ref = now != null ? now : Date.now()
  if (forgetAt <= ref) {
    resolved.delete(key)
    return false
  }
  return true
}

export function clearResolvedApprovals () {
  resolved.clear()
}
